import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CheckCircle, Target, Award, Users } from 'lucide-react';

const About = () => {
  const philosophy = [
    "Ethical training practices with no false promises",
    "Structured guidance aligned with exam requirements",
    "Real exam simulation for authentic experience",
    "Focus on consistency and precision in every procedure"
  ];

  const expectations = [
    {
      title: "Discipline & Daily Practice",
      description: "We expect students to maintain consistent practice schedules and show dedication to their training"
    },
    {
      title: "Openness to Feedback",
      description: "Success requires willingness to accept constructive criticism and implement improvements"
    },
    {
      title: "Strong Commitment",
      description: "Full dedication to the intensive training program and exam preparation process"
    }
  ];

  const whatWeProvide = [
    {
      title: "Realistic Exam Exposure",
      description: "Training in environments that replicate actual examination conditions"
    },
    {
      title: "Continuous Feedback",
      description: "Regular assessment and detailed feedback after each practice session"
    },
    {
      title: "Rubric-Based Evaluation",
      description: "Assessment aligned with actual examination rubrics and criteria"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1e3a8a] to-[#2563eb] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              About Overseas Dental Achievers
            </h1>
            <p className="text-lg md:text-xl text-gray-100">
              Building reputation through results, not marketing
            </p>
          </div>
        </div>
      </section>

      {/* About ODA */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Who We Are
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Overseas Dental Achievers is a state-of-the-art clinical training academy dedicated to preparing dentists for international licensing exams including ADC Part 2, NDECC, US Bench Test and Irish Dental Council exams.
              </p>
              <p className="text-gray-600 mb-6">
                Located in Chandigarh, India, we provide comprehensive hands-on training in a world-class facility equipped with the latest dental equipment and simulation technology.
              </p>
              <Button asChild className="bg-[#1e3a8a] hover:bg-[#1e3a8a]/90">
                <Link to="/contact">Get in Touch</Link>
              </Button>
            </div>
            <div className="bg-[#1e3a8a]/5 rounded-2xl p-8 border-2 border-[#1e3a8a]/20">
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1e3a8a] rounded-full mb-4">
                    <Users className="h-8 w-8 text-white" />
                  </div>
                  <div className="text-3xl font-bold text-[#1e3a8a] mb-2">1000+</div>
                  <div className="text-sm text-gray-600">Dentists Trained</div>
                </div>
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1e3a8a] rounded-full mb-4">
                    <Award className="h-8 w-8 text-white" />
                  </div>
                  <div className="text-3xl font-bold text-[#1e3a8a] mb-2">95%+</div>
                  <div className="text-sm text-gray-600">Success Rate</div>
                </div>
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1e3a8a] rounded-full mb-4">
                    <Target className="h-8 w-8 text-white" />
                  </div>
                  <div className="text-3xl font-bold text-[#1e3a8a] mb-2">4</div>
                  <div className="text-sm text-gray-600">Exam Programs</div>
                </div>
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1e3a8a] rounded-full mb-4">
                    <CheckCircle className="h-8 w-8 text-white" />
                  </div>
                  <div className="text-3xl font-bold text-[#1e3a8a] mb-2">13+</div>
                  <div className="text-sm text-gray-600">Years Experience</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Our Story
              </h2>
            </div>
            
            <div className="space-y-6 text-lg text-gray-600">
              <p>
                Overseas Dental Achievers began with a simple vision: to provide ethical, high-quality training for dentists preparing for international licensing examinations. Our journey started with just 18 candidates in our first batch, and the results spoke for themselves—17 of them passed their examinations.
              </p>
              <p>
                This remarkable success was not achieved through aggressive marketing or tall promises, but through structured training, honest guidance, and a deep commitment to our students' success. Word of mouth became our strongest advocate, and over the years, we've grown to train over 1000 dentists from around the world.
              </p>
              <p>
                Today, ODA stands as a trusted name in international dental exam preparation. Our reputation has been built entirely on results—the success stories of dentists who walked through our doors with dreams and left equipped with the skills, confidence, and knowledge to achieve their international dental career goals.
              </p>
              <p>
                We continue to maintain the same principles that guided us from day one: ethical practices, structured learning, realistic simulation, and unwavering focus on student success.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Philosophy */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Philosophy
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {philosophy.map((item, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm border-2 border-gray-100 hover:border-[#1e3a8a] transition-colors">
                <CheckCircle className="h-8 w-8 text-[#1e3a8a] mb-4" />
                <p className="text-gray-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Expect From Candidates */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What We Expect From Candidates
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Success requires partnership between our training and your commitment
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {expectations.map((item, index) => (
              <div key={index} className="bg-white p-8 rounded-lg shadow-sm">
                <div className="w-12 h-12 bg-[#1e3a8a] rounded-full flex items-center justify-center text-white text-xl font-bold mb-4">
                  {index + 1}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-600">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Provide */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What We Provide
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive support for your exam preparation journey
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {whatWeProvide.map((item, index) => (
              <div key={index} className="bg-[#1e3a8a] text-white p-8 rounded-lg shadow-sm">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-white text-xl font-bold mb-4">
                  {index + 1}
                </div>
                <h3 className="text-xl font-semibold mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-200">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#1e3a8a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Start Your Journey?
          </h2>
          <p className="text-lg text-gray-200 mb-8 max-w-2xl mx-auto">
            Join the ODA family and take the first step toward your international dental career
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="secondary" className="bg-white text-[#1e3a8a] hover:bg-gray-100">
              <Link to="/courses">Explore Courses</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
